/**
 * useAssetApplicationParents
 *
 * Post-pagination enrichment hook that resolves each member observation's
 * parent :Application via the `HAS_MEMBER` reverse relationship and returns
 * the matched parent's stable identity ({ key, fqdn }). The asset `/my`
 * call returns a flat list and does NOT walk HAS_MEMBER on its own — this
 * hook fans out one extra tree query keyed on the page's member keys and
 * merges the result into a `Map<memberKey, ParentApplication>`.
 *
 * Reroute-fragility: the parent FQDN is read from the matched Application
 * object's `fqdn` field, NOT from the member's own `group` (which becomes
 * incorrect after dynamic reroutes once subdomains route under a root).
 *
 * Cost model: one tree query per asset page (≤ page size / default 100).
 * Disabled when the application-drawer feature gate is off — the table
 * doesn't surface any Application-aware column or routing in that case.
 *
 * @see rollup-frontend-plan.md item A
 */
import { useMemo } from 'react';

import { useApplicationDrawerEnabled } from '@/hooks/useApplicationDrawerEnabled';
import { useMy } from '@/hooks/useMy';
import type { Asset, NodeLabel, Operator, TreeNode } from '@/types';

export interface ApplicationParent {
  /** Opaque `#application#<uuid>` key — durable across membership reroutes. */
  key: string;
  /** Human-readable parent FQDN. */
  fqdn: string;
}

export interface UseAssetApplicationParentsResult {
  /** Map of member asset key → parent Application identity. */
  parentsByAssetKey: Map<string, ApplicationParent>;
  /** True while the enrichment query is in-flight. */
  isLoading: boolean;
}

/**
 * Given a list of asset keys returned by the main asset table query,
 * fetch each one's parent :Application (when one exists) and expose a
 * `key -> { key, fqdn }` map keyed on the *member* asset key.
 *
 * Pass the page-of-keys (NOT individual observations) so React-Query
 * cache lookups stabilize across renders.
 */
export function useAssetApplicationParents(
  memberAssetKeys: string[]
): UseAssetApplicationParentsResult {
  const applicationDrawerEnabled = useApplicationDrawerEnabled();

  // Stable dedup-sort so the query key is hash-stable across renders that
  // mutate ordering (e.g. tree-format dedup pass) but not membership.
  const sortedKeys = useMemo(() => {
    if (!memberAssetKeys?.length) return [];
    const unique = Array.from(new Set(memberAssetKeys.filter(Boolean)));
    return unique.sort();
  }, [memberAssetKeys]);

  const enabled = applicationDrawerEnabled && sortedKeys.length > 0;

  // Tree query — find all Applications whose HAS_MEMBER edge lands on
  // one of the page's member keys, returning the Application object so
  // we can pull `fqdn` from the source-of-truth node.
  const { data = [], isLoading } = useMy(
    {
      resource: 'asset',
      tree: true,
      query: {
        node: {
          labels: ['Application'] as NodeLabel[],
          relationships: [
            {
              label: 'HAS_MEMBER',
              target: {
                labels: ['Asset'] as NodeLabel[],
                filters: [
                  {
                    field: 'key',
                    operator: 'IN' as Operator,
                    value: [sortedKeys],
                  },
                ],
              },
            },
          ],
        },
      },
    },
    { enabled }
  );

  const parentsByAssetKey = useMemo(() => {
    const map = new Map<string, ApplicationParent>();
    if (!data?.length) return map;

    for (const treeNode of data as TreeNode[]) {
      const result = treeNode?.node?.result as
        | Record<string, unknown[]>
        | undefined;
      const apps = (result?.applications as Asset[] | undefined) ?? [];
      const parent = apps[0];
      if (!parent?.key) continue;

      // Per-Application HAS_MEMBER edges → matched member keys. The
      // FQDN comes from the matched parent node, NOT from member.group.
      const parentIdentity: ApplicationParent = {
        key: parent.key,
        fqdn:
          (parent as Asset & { fqdn?: string }).fqdn ||
          parent.group ||
          parent.identifier ||
          '',
      };

      const memberRels = treeNode?.node?.relationships ?? [];
      for (const memberRel of memberRels) {
        if (memberRel.label !== 'HAS_MEMBER') continue;
        const memberResult = memberRel.target?.result as
          | Record<string, unknown[]>
          | undefined;
        if (!memberResult) continue;

        // Members come back bucketed by type (same shape as
        // flattenAssetGraphResponse). Walk every bucket.
        for (const bucket of Object.values(memberResult)) {
          if (!Array.isArray(bucket)) continue;
          for (const member of bucket as Asset[]) {
            if (member?.key) {
              map.set(member.key, parentIdentity);
            }
          }
        }
      }
    }
    return map;
  }, [data]);

  return { parentsByAssetKey, isLoading };
}
